import { Component, effect, inject, input, untracked } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  AttachmentDetailDto,
  CustomFieldCollectionDetailDto,
  CustomFieldCollectionInstanceDetailDto,
  CustomFieldCollectionInstanceMergeDto,
  CustomFieldCollectionMemberItemDto,
  CustomFieldCompositionDto,
  CustomFieldItemDto,
  CustomFieldValueMergeDto,
  CustomFieldValueWrapperDto,
  FileUploadService,
} from '@artemis-software/wr-api';
import { ArrayRequiredValidator } from '@/utils/validators';
import { computeHiddenFieldIds } from '@shared/custom-form/custom-field-visibility';
import { take } from 'rxjs';

export interface CustomFieldFormElement {
  formControl: FormControl | FormArray;
  memberId: string;
  field: CustomFieldItemDto;
  required: boolean;
  colSpan: number;
  /** True when the underlying field has been soft-deleted globally. Drives the tombstone icon. */
  fieldDeleted: boolean;
  /** True when this member was removed from the collection (but the field may still exist). */
  memberDeleted: boolean;
  /** Required AND not grandfathered away — the validator is only attached while also visible. */
  enforceRequired: boolean;
  visibleIfFieldId: string | null;
  visibleIfValue: string | null;
  hasBaselineValue: boolean;
}

type UnwrapValue = keyof CustomFieldValueWrapperDto;

/** What the attachment controls hold: the DTO plus the local `deleted` flag the carousel toggles. */
type FormAttachment = AttachmentDetailDto & { deleted: boolean };

const isWrapperEmpty = (wrapper: CustomFieldValueWrapperDto): boolean =>
  Object.values(wrapper).every(v => v === null || v === undefined || v === '');

// Both validators are stateless, so one shared instance per kind is enough — allocating a fresh
// closure per control per applyVisibility() run showed up on every keystroke.
const arrayRequiredValidator = ArrayRequiredValidator.createValidator();

class CustomForm {
  collection: CustomFieldCollectionDetailDto;
  form: FormGroup;
  elements: CustomFieldFormElement[];
  /** Assigned by applyVisibility() — a getter here would rebuild the array on every CD cycle. */
  visibleElements: CustomFieldFormElement[] = [];
  private readonly baseline: Map<string, CustomFieldValueWrapperDto[]>;
  private hidden = new Set<string>();
  private visibilityApplied = false;

  /**
   * The form structure is built from an explicit list of members. The caller decides whether
   * those come from the live collection (schema mode, for new entities) or from a saved
   * instance's values (snapshot mode, for historical protocols). `collection` is retained for
   * its id (used in getMergeDto) and metadata.
   *
   * @param isNewRecord true when no persisted instance exists yet — required fields are then
   *   always enforced. When false (editing an existing record), a required field is enforced
   *   only if it already had a value when the record was loaded — see `baseline`, built from
   *   `instance`. A field the catalog made required afterwards must not retroactively block
   *   the save (Freshdesk #213/#250).
   */
  constructor(
    collection: CustomFieldCollectionDetailDto,
    members: CustomFieldCollectionMemberItemDto[],
    private readonly isNewRecord: boolean,
    instance?: CustomFieldCollectionInstanceDetailDto,
  ) {
    this.collection = collection;
    this.baseline = new Map(
      instance?.values?.map(v => [v.member.id, v.values ?? []]) ?? [],
    );
    this.elements = members.map(member => this.createElement(member));
    this.form = new FormGroup({});
    this.elements.forEach(element => {
      const controlKey = element.field.id;
      this.form.addControl(controlKey, element.formControl);
    });
    this.applyVisibility();
    this.form.valueChanges.subscribe(() => this.applyVisibility());
  }

  createElement(member: CustomFieldCollectionMemberItemDto): CustomFieldFormElement {
    const field = member.field;
    const savedValues = this.baseline.get(member.id) ?? [];
    const hasBaselineValue = savedValues.some(v => !isWrapperEmpty(v));
    // A rule-bearing member is never grandfathered: "no value at load time" cannot tell legacy data
    // apart from a field that was simply hidden when the record was created, and reading it as
    // legacy would leave a re-appearing required field permanently without a validator. The
    // grandfathering itself (Freshdesk #213/#250) stays intact for every member without a rule.
    const hasRule = !!member.visibleIfFieldId;
    const enforceRequired = (member.required ?? false) && (this.isNewRecord || hasBaselineValue || hasRule);

    // Validators are attached by applyVisibility(), not here: a hidden required field must not
    // make the form invalid, and visibility can flip on every keystroke.
    let control;
    if (field.composition.cardinalityType === CustomFieldCompositionDto.CardinalityTypeEnum.Multiple) {
      control = new FormArray([new FormControl()]);
    } else {
      control = new FormControl(null);
    }

    return {
      formControl: control,
      memberId: member.id,
      field: field,
      required: member.required ?? false,
      colSpan: member.colSpan ?? 12,
      fieldDeleted: field.deleted ?? false,
      memberDeleted: member.deleted ?? false,
      enforceRequired,
      visibleIfFieldId: member.visibleIfFieldId ?? null,
      visibleIfValue: member.visibleIfValue ?? null,
      hasBaselineValue,
    };
  }

  isHidden(fieldId: string): boolean {
    return this.hidden.has(fieldId);
  }

  /**
   * Recomputes the hidden set and re-attaches the required validators accordingly (WR-2292).
   * Removing the validator — rather than only filtering the error message — is what keeps
   * `form.invalid` from blocking a save on a field the user can't even see.
   *
   * Runs on every keystroke, so it only touches controls whose visibility actually flipped and
   * defers the group-level recomputation to a single call: `updateValueAndValidity` bubbles to the
   * parent by default, which made a form of N fields re-aggregate N times per keystroke.
   */
  applyVisibility(): void {
    const previous = this.hidden;
    this.hidden = computeHiddenFieldIds(
      this.elements.map(element => ({
        fieldId: element.field.id,
        valueType: element.field.composition.valueType,
        visibleIfFieldId: element.visibleIfFieldId,
        visibleIfValue: element.visibleIfValue,
        hasBaselineValue: element.hasBaselineValue,
        memberDeleted: element.memberDeleted,
      })),
      fieldId => this.form.get(fieldId)?.value,
    );

    // First run attaches every validator; later runs only fix up what actually flipped.
    const changed = this.visibilityApplied
      ? this.elements.filter(element => previous.has(element.field.id) !== this.hidden.has(element.field.id))
      : this.elements;
    this.visibilityApplied = true;
    if (!changed.length) return;

    this.visibleElements = this.elements.filter(element => !this.hidden.has(element.field.id));
    changed.forEach(element => {
      const control = this.form.get(element.field.id);
      if (!control) return;
      const enforce = element.enforceRequired && !this.hidden.has(element.field.id);
      const validator = control instanceof FormArray ? arrayRequiredValidator : Validators.required;
      control.setValidators(enforce ? [validator] : []);
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
    this.form.updateValueAndValidity({ emitEvent: false });
  }
}

export const unwrapDtoValue = (dto: CustomFieldValueWrapperDto): UnwrapValue | null => {
  const nonNullValues = Object.values(dto).filter(v => v !== null && v !== undefined);
  return nonNullValues.length > 0 ? nonNullValues[0] : null;
};

export const unwrapDtoValues = (dtos: CustomFieldValueWrapperDto[], type: CustomFieldCompositionDto.CardinalityTypeEnum) => {
  if (type === CustomFieldCompositionDto.CardinalityTypeEnum.Multiple) {
    return dtos.map(dto => unwrapDtoValue(dto)) as UnwrapValue[];
  }
  if (dtos.length > 0) {
    if (dtos.length > 1) {
      throw new Error(`Expected 0 or 1 values but got ${dtos.length}`);
    }
    return dtos.map(dto => unwrapDtoValue(dto))[0] as UnwrapValue;
  }
  return null;
};

const wrapDtoValue = (value: unknown, type: CustomFieldCompositionDto.ValueTypeEnum): CustomFieldValueWrapperDto => {
  switch (type) {
    case 'Boolean':
      return { booleanValue: value as boolean };
    case 'Integer':
      return { intValue: value as number };
    case 'Double':
      return { doubleValue: value as number };
    case 'String':
      return { stringValue: value as string };
    case 'Attachment':
      return { attachmentKey: (value as FormAttachment | null)?.s3key };
    case 'Longtext':
    case 'LongtextWithPresets':
      return { longtextValue: value as string };
    case 'MultipleChoice':
      return { multipleChoicePicks: value as string };
    case 'LocalDate':
      return { localDate: value as string };
    case 'LocalDateTime':
      return { localDateTime: value as string };
    case 'Table':
    case 'Signature':
      return { longtextValue: value as string };
  }
  throw new Error(`Unknown type ${type}`);
};

@Component({
  standalone: false,
  selector: 'wr-custom-form',
  templateUrl: './custom-form.component.html',
  styleUrls: ['./custom-form.component.scss'],
})
export class CustomFormComponent {

  private readonly fileUploadService = inject(FileUploadService);

  readonly collection = input<CustomFieldCollectionDetailDto>();

  /**
   * Aliased so callers keep binding `[readonly]` — disables/enables the built form. The alias is
   * load-bearing: `readonly` is a reserved word in a class body, so the property cannot carry the
   * binding's name, and renaming the binding would break every caller.
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  readonly readonlyInput = input(false, { alias: 'readonly' });

  readonly instance = input<CustomFieldCollectionInstanceDetailDto>();

  /**
   * When true, the form STRUCTURE is built from the saved instance's values (a frozen
   * snapshot of which fields existed when the entity was filled in), instead of the live
   * collection schema. Used by the protocol detail page so historical protocols render
   * exactly the fields they were saved with — deleted fields stay visible, newly added
   * template fields do not leak in. Defaults to false (live schema), preserving the existing
   * behavior for new-entity forms (protocol creation, QR finish) and for TGAs.
   */
  readonly renderFromInstance = input(false);

  readonly singleColumn = input(false);

  customForm?: CustomForm;

  constructor() {
    effect(() => this.rebuild());
    effect(() => {
      const readonly = this.readonlyInput();
      if (!this.customForm) return;
      if (readonly) {
        this.customForm.form.disable();
      } else {
        this.customForm.form.enable();
      }
    });
  }

  /**
   * (Re)build the form whenever collection / instance / renderFromInstance changes.
   * Idempotent: it no-ops until a collection is present, then picks the structural
   * member source per the render mode. Whether required fields are grandfathered is
   * derived here, not caller-chosen: an existing persisted instance means legacy data.
   */
  private rebuild(): void {
    const collection = this.collection();
    if (!collection) return;

    const instance = this.instance();
    const snapshotMembers = instance?.values
      ?.map(value => value.member)
      ?.filter((member): member is NonNullable<typeof member> => !!member);

    const members = (this.renderFromInstance() && snapshotMembers && snapshotMembers.length > 0)
      ? this.restoreRuleBearingMembers(snapshotMembers, collection.members)
      : collection.members;

    this.customForm = new CustomForm(collection, members, instance?.id == null, instance);
    if (untracked(() => this.readonlyInput())) {
      this.customForm.form.disable();
    } else {
      this.customForm.form.enable();
    }
    this.updateValues(instance);
  }

  /**
   * A member that was hidden when the record was saved has no value row (WR-2292), so it is absent
   * from the snapshot and the snapshot render would drop it for good — flipping its trigger back
   * would never bring it into the form again. Members carrying a rule are therefore taken from the
   * live collection instead. Rule-less members stay excluded on purpose: for those, absence from the
   * snapshot means the field was not part of the group yet, and re-adding them would leak newly
   * added template fields into historical records, which is exactly what snapshot mode prevents.
   *
   * Matched on field id, not member id: the form keys its controls by field id, so a member the
   * snapshot already covers through a tombstoned predecessor must not be added a second time.
   */
  private restoreRuleBearingMembers(
    snapshot: CustomFieldCollectionMemberItemDto[],
    live: CustomFieldCollectionMemberItemDto[],
  ): CustomFieldCollectionMemberItemDto[] {
    const snapshotFieldIds = new Set(snapshot.map(member => member.field.id));
    const missing = live.filter(member => !!member.visibleIfFieldId && !snapshotFieldIds.has(member.field.id));
    if (!missing.length) return snapshot;
    return [...snapshot, ...missing].sort((a, b) => a.ranking - b.ranking);
  }

  updateValues(instance?: CustomFieldCollectionInstanceDetailDto) {
    if (this.customForm && instance && !this.customForm.form.dirty) {
      instance.values?.forEach(valueDetail => {
        const field = valueDetail.member.field;
        const control = this.customForm?.form.get(field.id);
        if (control) {
          if (field.composition.valueType === 'Attachment') {
            const wrapperDtos = (valueDetail.values ?? []).filter(Boolean);
            if (control instanceof FormArray) {
              control.clear();
              wrapperDtos.forEach(dto => {
                const fc = new FormControl(this.attachmentFromWrapperDto(dto));
                control.push(fc);
                if (!dto.attachmentDetail && dto.attachmentKey) {
                  this.resolvePresignedUrl(dto.attachmentKey, fc);
                }
              });
            } else {
              const dto = wrapperDtos[0];
              if (!dto) { control.setValue(null); return; }
              control.setValue(this.attachmentFromWrapperDto(dto));
              if (!dto.attachmentDetail && dto.attachmentKey) {
                this.resolvePresignedUrl(dto.attachmentKey, control);
              }
            }
            return;
          }
          const value = unwrapDtoValues(valueDetail.values ?? [], field.composition.cardinalityType);
          if (control instanceof FormArray) {
            console.assert(Array.isArray(value), 'Expected value to be an array');
            control.clear();
            (value as UnwrapValue[] | null)?.forEach(v => control.push(new FormControl(v)));
          } else {
            control.setValue(value);
          }
        }
      });
    }
  }

  /** Field names of required fields that are currently blocking the save (empty + enforced). */
  getMissingRequiredFieldNames(): string[] {
    if (!this.customForm) return [];
    return this.customForm.elements
      .filter(element => !this.customForm?.isHidden(element.field.id))
      .filter(element => element.required && !!this.customForm?.form.get(element.field.id)?.errors?.['required'])
      .map(element => element.field.name);
  }

  get sectionTitle(): string {
    return this.customForm?.collection.title ?? '';
  }

  getMergeDto(): CustomFieldCollectionInstanceMergeDto {
    if (!this.customForm) throw new Error('customForm is undefined');
    return {
      id: this.instance()?.id,
      collectionId: this.customForm.collection.id,
      values: this.customForm.elements.map(element => {
        const control = this.customForm?.form.get(element.field.id);
        // A hidden field is stored as "no value" — it is not part of this record's answers.
        const hidden = this.customForm?.isHidden(element.field.id) ?? false;
        const value = control?.value;
        const values = hidden ? [] : (Array.isArray(value) ? value : [value]);

        return {
          memberId: element.memberId,
          values: values.map((value: unknown) => wrapDtoValue(value, element.field.composition.valueType)),
        } as CustomFieldValueMergeDto;
      }),
    };
  }

  markAsPristine() {
    this.customForm?.form.markAsPristine();
  }

  private attachmentFromWrapperDto(dto: CustomFieldValueWrapperDto): FormAttachment | null {
    if (dto.attachmentDetail) {
      return { ...dto.attachmentDetail, deleted: false };
    }
    if (dto.attachmentKey) {
      return {
        s3key: dto.attachmentKey,
        presignedUrl: '',
        deleted: false,
        comment: '',
        rotationDegrees: 0,
        created: '',
        tags: [],
        type: AttachmentDetailDto.TypeEnum.File,
      };
    }
    return null;
  }

  private resolvePresignedUrl(s3key: string, control: AbstractControl): void {
    this.fileUploadService.getPresignedObject(s3key).pipe(take(1)).subscribe(result => {
      const current = control.value;
      if (current?.s3key === s3key) {
        control.setValue({ ...current, presignedUrl: result.url });
      }
    });
  }
}
